import { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { getCustomerIsAuthenticated } from "../../utils/identity";
import { getCustomerSegmentIds } from "../../utils/customerSegments";
import getReturnsChargeMessaging from "../../api/returnsChargeMessagingApi";
import { useRuntimeConstants } from "../runtimeConstants/RuntimeConstantsContext";

const PersonalisedReturnsMessagesContext = createContext({
  isLoading: false,
  returnsChargeMessage: null,
});

export const PersonalisedReturnsMessagesProvider = ({ children }) => {
  const [personalisedReturnsMessages, setPersonalisedReturnsMessages] =
    useState({
      isLoading: false,
      returnsChargeMessage: null,
    });

  const {
    features: { personalisedReturnsMessageEnabled },
  } = useRuntimeConstants();

  useEffect(() => {
    if (!personalisedReturnsMessageEnabled) {
      return;
    }

    const fetchPersonalisedReturnsMessages = async () => {
      const isAuthenticated = await getCustomerIsAuthenticated();
      if (!isAuthenticated) {
        setPersonalisedReturnsMessages({
          isLoading: false,
          returnsChargeMessage: null,
        });
        return;
      }

      setPersonalisedReturnsMessages((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
      let segmentIds;
      try {
        segmentIds = await getCustomerSegmentIds();
      } catch {
        setPersonalisedReturnsMessages({
          isLoading: false,
          returnsChargeMessage: null,
        });
        return;
      }
      if (segmentIds) {
        try {
          const returnsChargeMessage = await getReturnsChargeMessaging(
            segmentIds
          );
          setPersonalisedReturnsMessages({
            isLoading: false,
            returnsChargeMessage,
          });
        } catch {
          setPersonalisedReturnsMessages({
            isLoading: false,
            returnsChargeMessage: null,
          });
        }
      } else {
        setPersonalisedReturnsMessages({
          isLoading: false,
          returnsChargeMessage: null,
        });
      }
    };

    fetchPersonalisedReturnsMessages();
  }, [personalisedReturnsMessageEnabled]);

  return (
    <PersonalisedReturnsMessagesContext.Provider
      value={personalisedReturnsMessages}
    >
      {children}
    </PersonalisedReturnsMessagesContext.Provider>
  );
};

PersonalisedReturnsMessagesProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const usePersonalisedReturnsMessages = () =>
  useContext(PersonalisedReturnsMessagesContext);
